import { ReactNode } from "react";
import styles from "./BpsDataTable.module.scss";
import { BpsTablePagination } from "./BpsTablePagination";
import { BpsPageHeader } from "../page-header/BpsPageHeader";

export type BpsColumn<T> = {
	header: string;
	accessor: string;
	width?: string | number;
	render?: (record: T) => ReactNode;
};

interface BpsDataTableProps<T> {
	title: string;
	/** Primary action rendered next to the title, e.g. a "New …" button. */
	headerAction?: ReactNode;
	searchPlaceholder?: string;
	onSearch?: (value: string) => void;
	columns: BpsColumn<T>[];
	data: T[];
	loading?: boolean;
	emptyComponent?: ReactNode;
	onRowClick?: (record: T) => void;
	page: number;
	pageSize: number;
	totalItems: number;
	onPageChange: (page: number) => void;
}

const getNestedValue = (obj: any, path: string) =>
	path.split(".").reduce((prev, curr) => (prev && prev[curr] !== undefined ? prev[curr] : undefined), obj);

/**
 * List shell of the BPS admin portal: title + action, search, table and pagination
 * in one component. Separate from the xepp DataTable on purpose — that one is
 * shared by 15 utility-admin pages and carries their blue palette, while this one
 * follows the BPS brand tokens.
 */
function BpsDataTable<T extends { id?: string | number }>({
	title,
	headerAction,
	searchPlaceholder,
	onSearch,
	columns,
	data,
	loading = false,
	emptyComponent,
	onRowClick,
	page,
	pageSize,
	totalItems,
	onPageChange,
}: Readonly<BpsDataTableProps<T>>) {
	const renderCell = (record: T, column: BpsColumn<T>) => {
		if (column.render) return column.render(record);

		const value = getNestedValue(record, column.accessor);
		if (value === null || value === undefined || value === "") return "N/A";

		return String(value);
	};

	return (
		<div className={styles.container}>
			<BpsPageHeader
				title={title}
				action={headerAction}
				onSearch={onSearch}
				searchPlaceholder={searchPlaceholder}
			/>

			<div className={styles.tableWrapper}>
				<table className={styles.table}>
					<thead>
						<tr>
							{columns.map(column => (
								<th key={`${column.accessor}-${column.header}`} style={{ width: column.width }}>
									{column.header}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{loading && (
							<tr>
								<td colSpan={columns.length} className={styles.stateCell}>
									Loading...
								</td>
							</tr>
						)}

						{!loading && data.length === 0 && (
							<tr>
								<td colSpan={columns.length} className={styles.stateCell}>
									{emptyComponent ?? "No data available"}
								</td>
							</tr>
						)}

						{!loading &&
							data.map((record, index) => (
								<tr
									key={record.id ?? index}
									className={onRowClick ? styles.clickable : ""}
									onClick={onRowClick ? () => onRowClick(record) : undefined}
								>
									{columns.map(column => (
										<td
											key={`${column.accessor}-${column.header}`}
											style={{ width: column.width, maxWidth: column.width }}
										>
											{renderCell(record, column)}
										</td>
									))}
								</tr>
							))}
					</tbody>
				</table>
			</div>

			<BpsTablePagination page={page} pageSize={pageSize} totalItems={totalItems} onPageChange={onPageChange} />
		</div>
	);
}

export default BpsDataTable;
